import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router";
import {
  ArrowLeft,
  TrendingUp,
  Target,
  CheckCircle2,
  Circle,
  Lock,
  FileText,
  Plus,
  CalendarDays,
  BookOpen,
  UserCircle,
  Edit,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useProjects } from "@/hooks/useProjects";
import { useSpecLists, useSpecListsForSpecificMask } from "@/hooks/useSpecLists";
import { useChipPosByMask } from "@/hooks/useChipPos";
import { useTestLabelsByMask } from "@/hooks/useTestLabels";
import { useMeasurementRequestsByMask } from "@/hooks/useMeasurementRequests";
import { useMaskStructures, useProjectMasks } from "@/hooks/useMasks";
import { useMasks } from "@/hooks/useMasks";
import { usePcmVersions } from "@/hooks/usePcmVersions";
import { Combobox } from "@/components/ui/combobox";
import { WorkflowStep } from "@/types/maskWorkflow";
import { calculateStepStatuses } from "@/utils/workflowUtils";
import { EditProjectDialog } from "@/components/projects/ProjectDialog";
import {
  CreateMeasurementRequestDialog,
  CreateTestFlightRequestDialog,
} from "@/components/measurementRequests/MeasurementRequestDialog";
import { CreateMaskDialog } from "@/components/projects/maskDialog/MaskDialog";
import NoMaskSelectedCard from "@/components/projects/NoMaskSelectedCard";
import ChipPosUploadStep from "@/components/projects/steps/ChipPosUploadStep";
import { LoadingDisplay } from "@/components/shared/LoadingDisplay";
import { GenericErrorDisplay } from "@/components/shared/ErrorDisplay";
import { NotFoundDisplay } from "@/components/shared/NotFoundDisplay";
import { CreateSpecListDialog } from "@/components/specLists/CreateSpecListDialog";
import ProjectSpecsListStep from "@/components/projects/steps/ProjectSpecListStep";
import Tooltip from "@/components/shared/Tooltip";
import TestLabelsStep from "@/components/projects/steps/TestLabelsStep";
import { CreateTestLabelDialog } from "@/components/testlabels/CreateTestLabelDialog";
import { MeasurementRequestStep } from "@/components/projects/steps/MeasurementRequestStep";
import { MaskDetailsStepCard } from "@/components/projects/steps/MaskDetailsStepCard";
import { DesignUploadStep } from "@/components/projects/steps/DesignUploadStep";

const initialWorkflowSteps: WorkflowStep[] = [
  {
    id: 1,
    name: "Create mask",
    description: "Basic mask information",
    role: "Project Manager",
    isCompleted: false,
    isCurrent: false,
    isLocked: true,
  },
  {
    id: 2,
    name: "Design XML",
    description: "Link a set of design XML files for this mask",
    role: "Testing Team",
    isCompleted: false,
    isCurrent: false,
    isLocked: true,
  },
  {
    id: 3,
    name: "Chip Position Files",
    description: "Upload chip positioning files",
    role: "Project Manager",
    isCompleted: false,
    isCurrent: false,
    isLocked: true,
  },
  {
    id: 4,
    name: "Project Specs List",
    description: "Create at least one specs list for this mask",
    role: "Project Manager",
    isCompleted: false,
    isCurrent: false,
    isLocked: true,
  },
  {
    id: 5,
    name: "Test Labels",
    description:
      "Create one or more Test Labels for the mask and validate them through a test flight",
    role: "Project Manager",
    isCompleted: false,
    isCurrent: false,
    isLocked: true,
  },
  {
    id: 6,
    name: "Measurement Requests",
    description: "List of all running and completed requests",
    role: "Project Manager",
    isCompleted: false,
    isCurrent: false,
    isLocked: true,
  },
];

const getMaskWorkflowSteps = (
  maskSelected: boolean,
  hasSpecLists: boolean,
  hasChipPos: boolean,
  chipPosError: boolean,
  _hasTestLabels: boolean,
  atLeastOneTestLabelValidated: boolean,
  standardStructuresCount: number,
  customStructuresCount: number,
): WorkflowStep[] => {
  return [
    {
      id: 1,
      name: "Create mask",
      description: "Basic mask information",
      role: "Project Manager",
      isCompleted: maskSelected,
      isCurrent: false, // Will be calculated by utility function
      isLocked: false, // Will be calculated by utility function
    },
    {
      id: 2,
      name: "Design XML",
      description: "Link a set of design XML files for this mask",
      role: "Testing Team",
      isCompleted: standardStructuresCount + customStructuresCount > 0,
      isCurrent: false, // Will be calculated by utility function
      isLocked: false, // Will be calculated by utility function
    },
    {
      id: 3,
      name: "Chip Position Files",
      description: "Upload chip positioning files",
      role: "Project Manager",
      isCompleted: hasChipPos && !chipPosError,
      isCurrent: false, // Will be calculated by utility function
      isLocked: false, // Will be calculated by utility function
    },
    {
      id: 4,
      name: "Project Specs List",
      description: "Create at least one specs list for this mask",
      role: "Project Manager",
      isCompleted: hasSpecLists || (customStructuresCount > 0 && standardStructuresCount === 0), // If there are only custom structures, we consider this step completed
      isCurrent: false, // Will be calculated by utility function
      isLocked: false, // Will be calculated by utility function
    },
    {
      id: 5,
      name: "Test Labels",
      description:
        "Create one or more Test Labels for the mask and validate them through a test flight",
      role: "Project Manager",
      isCompleted: atLeastOneTestLabelValidated,
      isCurrent: false, // Will be calculated by utility function
      isLocked: false, // Will be calculated by utility function
    },
    {
      id: 6,
      name: "Measurement Requests",
      description: "List of all running and completed requests",
      role: "Project Manager",
      isCompleted: false,
      isCurrent: false, // Will be calculated by utility function
      isLocked: false, // Will be calculated by utility function
    },
  ];
};

export function ProjectDetailPage() {
  const { id, maskId } = useParams<{ id: string; maskId?: string }>();
  const navigate = useNavigate();
  const { data: projects, isLoading: isProjectsLoading, error: projectsError } = useProjects();
  const {
    data: pcmVersions,
    isLoading: isPcmVersionsLoading,
    error: pcmVersionsError,
  } = usePcmVersions();

  // Fetch masks for this project
  const {
    data: projectMasks,
    isLoading: isMasksLoading,
    error: masksError,
    isValidating: isValidatingMasks,
  } = useProjectMasks(id);

  const [selectedMaskId, setSelectedMaskId] = useState<string | undefined>(maskId);
  const [isCreateMaskOpen, setIsCreateMaskOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<WorkflowStep | null>(null);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [isCreateSpecListOpen, setIsCreateSpecListOpen] = useState(false);
  const [isCreateTestLabelOpen, setIsCreateTestLabelOpen] = useState(false);
  const [isTestFlightDialogOpen, setIsTestFlightDialogOpen] = useState(false);
  const [isCreateMeasurementRequestDialogOpen, setIsCreateMeasurementRequestDialogOpen] =
    useState(false);
  const [testLabelIdForTestFlight, setTestLabelIdForTestFlight] = useState<number | null>(null);

  // Fetch all masks for the test flight dialog
  const { data: allMasks } = useMasks();

  // Sync selectedMaskId with URL parameter and validate
  useEffect(() => {
    if (maskId && maskId !== selectedMaskId) {
      // Check if the mask ID is valid
      const isValidMask = projectMasks.some((mask) => mask.id === maskId);
      if (isValidMask) {
        setSelectedMaskId(maskId);
      } else {
        if (isValidatingMasks) return;
        // If invalid mask ID, redirect to project page without mask
        navigate(`/projects/${id}`, { replace: true });
      }
      // If there's only one mask for the project then we automatically select it
    } else if (projectMasks.length === 1) {
      setSelectedMaskId(projectMasks[0].id);
    }
  }, [maskId, selectedMaskId, navigate, id, projectMasks, isValidatingMasks]);

  // Fetch spec lists for the selected mask
  const {
    data: specLists,
    isLoading: isSpecListsLoading,
    error: specListsError,
    mutate: refetchSpecListsForSpecificMask,
  } = useSpecListsForSpecificMask(selectedMaskId);

  const {
    data: allSpecLists,
    isLoading: isAllSpecListsLoading,
    error: allSpecListsError,
  } = useSpecLists();

  // Fetch chip position files for the selected mask
  const {
    data: chipPos,
    isLoading: isChipPosLoading,
    error: chipPosError,
    mutate: mutateChipPos,
  } = useChipPosByMask(selectedMaskId);

  // Fetch test labels for the selected mask
  const {
    data: testLabels,
    isLoading: isTestLabelsLoading,
    error: testLabelsError,
    mutate: mutateTestLabels,
  } = useTestLabelsByMask(selectedMaskId);

  // Fetch measurement requests for the selected mask
  const {
    data: measurementRequests,
    isLoading: isMeasurementRequestsLoading,
    error: measurementRequestsError,
  } = useMeasurementRequestsByMask(selectedMaskId);

  // Fetch structures for the selected mask to check if design XML is linked
  const {
    data: maskWithStructures,
    isLoading: isMaskStructuresLoading,
    mutate: mutateMaskStructures,
  } = useMaskStructures(selectedMaskId);

  // Check if all at least one test labels is validated
  const atLeastOneTestLabelValidated =
    testLabels.length > 0 && testLabels.some((label) => label.status === "VALIDATED");

  // Get structures count for design XML verification
  const structuresCountStandard =
    maskWithStructures?.structures?.filter((s) => !s.isCustom).length ?? 0;
  const structuresCountCustom =
    maskWithStructures?.structures?.filter((s) => s.isCustom).length ?? 0;

  const rawWorkflowSteps = getMaskWorkflowSteps(
    selectedMaskId !== "",
    specLists.length > 0,
    chipPos.length > 0,
    !!chipPosError,
    testLabels.length > 0,
    atLeastOneTestLabelValidated,
    structuresCountStandard,
    structuresCountCustom,
  );

  // Calculate step statuses using the utility function
  const workflowSteps = useMemo(() => {
    if (isMaskStructuresLoading || isSpecListsLoading || isChipPosLoading || isTestLabelsLoading) {
      return initialWorkflowSteps;
    }
    return calculateStepStatuses(rawWorkflowSteps);
  }, [
    isChipPosLoading,
    isMaskStructuresLoading,
    isSpecListsLoading,
    isTestLabelsLoading,
    rawWorkflowSteps,
  ]);

  const handleMaskSelection = (maskId: string) => {
    setSelectedMaskId(maskId);
    navigate(`/projects/${id}/${maskId}`, { replace: true });
  };

  const handleStepClick = (step: WorkflowStep) => {
    setSelectedRequirement(step);
  };

  const getWorkflowStepIcon = (step: WorkflowStep) => {
    if (step.isCompleted) return CheckCircle2;
    if (step.isLocked) return Lock;
    return Circle;
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "Not set";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const projectMaskComboboxOptions = useMemo(() => {
    return projectMasks.map((mask) => ({
      key: mask.id,
      value: mask.id,
      label: mask.name,
    }));
  }, [projectMasks]);

  if (isProjectsLoading || isMasksLoading || isPcmVersionsLoading || isAllSpecListsLoading) {
    return <LoadingDisplay />;
  }

  if (projectsError || masksError || pcmVersionsError || allSpecListsError) {
    return <GenericErrorDisplay />;
  }

  const project = projects.find((p) => p.id === parseInt(id || "0"));

  const getStepColor = (step: WorkflowStep) =>
    step.isCompleted
      ? "bg-green-50 border-green-200"
      : step.isCurrent
        ? "bg-blue-50 border-blue-200"
        : step.isLocked
          ? "bg-gray-50 border-gray-200"
          : "bg-white border-gray-200";

  const getStepIconColor = (step: WorkflowStep) =>
    step.isCompleted
      ? "text-green-600"
      : step.isCurrent
        ? "text-blue-600"
        : step.isLocked
          ? "text-gray-400"
          : "text-gray-600";

  const getSelectedRequirementColor = (step: WorkflowStep) =>
    selectedRequirement?.id === step.id
      ? "ring-2 ring-blue-500 bg-blue-50"
      : step.isCompleted
        ? "bg-green-50 hover:bg-green-100"
        : step.isLocked
          ? "bg-gray-50 hover:bg-gray-100 cursor-not-allowed"
          : "bg-white border hover:border-gray-300";

  const goBack = () => {
    navigate(-1);
  };

  if (!project) {
    return <NotFoundDisplay redirectUrl="/projects" text="Project not found" />;
  }

  return (
    <div className="relative mx-auto container p-8 space-y-6">
      <Tooltip
        asChild
        content="Go back"
        trigger={
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft />
          </Button>
        }
        side="right"
      />

      {/* Project Title and Info */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <CardTitle className="text-3xl font-bold">{project.name}</CardTitle>
              {project.description && (
                <p className="text-gray-600 leading-relaxed max-w-3xl">{project.description}</p>
              )}
            </div>
            <div className="ml-6 flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditProjectOpen(true)}
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Project
              </Button>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <UserCircle className="w-4 h-4" />
                <span>{project.owner}</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Timeline Information */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                Timeline
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium min-w-[80px]">Start Date:</span>
                  <span className="text-gray-900">
                    {project.startDate ? formatDate(project.startDate) : "Not set"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium min-w-[80px]">End Date:</span>
                  <span className="text-gray-900">
                    {project.endDate ? formatDate(project.endDate) : "Not set"}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Project Details
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium min-w-[60px]">WBS:</span>
                  <span className="text-gray-900">{project.wbs || "Not set"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium min-w-[60px]">Funding:</span>
                  <span className="text-gray-900">{project.funding || "Not set"}</span>
                </div>
              </div>
            </div>

            {/* Learning Goal */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Learning Goal
              </h4>
              <div className="text-sm">
                <p className="text-gray-600 leading-relaxed">
                  {project.learningGoal || "No learning goal specified"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mask Selection - Full Width */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Mask Selection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-3">
              <Combobox
                className="flex-1"
                onChange={handleMaskSelection}
                value={selectedMaskId}
                placeholder={
                  projectMasks.length === 0
                    ? "No masks available for this project"
                    : "Select a mask for this project..."
                }
                searchPlaceholder="Search masks"
                options={projectMaskComboboxOptions}
                fullWidth
              />

              <Button
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => setIsCreateMaskOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Mask
              </Button>
              {isCreateMaskOpen ? (
                <CreateMaskDialog
                  onOpenChange={setIsCreateMaskOpen}
                  open
                  pcmVersions={pcmVersions}
                  project={project}
                />
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Mask Workflow */}
        {selectedMaskId && (
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Mask Workflow
                  </CardTitle>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/docs/mask-workflow" className="text-blue-600 hover:text-blue-800">
                      View Documentation
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workflowSteps.map((step) => {
                    const StepIcon = getWorkflowStepIcon(step);

                    return (
                      <div key={step.id} className="space-y-3">
                        {/* Step Header - now clickable for simplified workflow */}
                        <div
                          onClick={() => handleStepClick(step)}
                          className={`flex items-center justify-between p-4 rounded-lg border ${getStepColor(step)} cursor-pointer hover:shadow-md transition-all ${getSelectedRequirementColor(step)}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center">
                              <StepIcon className={`w-6 h-6 ${getStepIconColor(step)}`} />
                            </div>
                            <div>
                              <h3
                                className={`font-semibold ${step.isLocked ? "text-gray-400" : "text-gray-900"}`}
                              >
                                Step {step.id}: {step.name}
                              </h3>
                              <p
                                className={`text-sm ${step.isLocked ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {step.description}
                              </p>
                              <p
                                className={`text-xs ${step.isLocked ? "text-gray-300" : "text-gray-500"}`}
                              >
                                {step.role}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {step.isCompleted && (
                              <Badge className="bg-green-100 text-green-800">Completed</Badge>
                            )}
                            {step.isCurrent && !step.isCompleted && (
                              <Badge className="bg-blue-100 text-blue-800">Current</Badge>
                            )}
                            {!step.isLocked && (
                              <span className="text-xs text-blue-500 opacity-60">
                                click for details
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Placeholder when no mask is selected */}
        {selectedMaskId ? null : <NoMaskSelectedCard />}

        {/* Details Card */}
        <div className={selectedMaskId ? "lg:col-span-1" : "lg:col-span-3"}>
          {selectedRequirement && (
            <>
              {/* Step 1: Create Mask */}
              {selectedRequirement?.id === 1 && (
                <MaskDetailsStepCard
                  selectedMaskId={selectedMaskId}
                  projectMasks={projectMasks}
                  project={project}
                  pcmVersions={pcmVersions}
                  chipPos={chipPos}
                />
              )}

              {/* Step 2: Design XML */}
              {selectedRequirement?.id === 2 && (
                <DesignUploadStep
                  refreshMaskData={mutateMaskStructures}
                  selectedMaskId={selectedMaskId!}
                />
              )}

              {/* Step 3: Chip Position Files */}
              {selectedRequirement?.id === 3 && selectedMaskId && (
                <ChipPosUploadStep
                  chipPosError={chipPosError}
                  chipPos={chipPos}
                  isChipPosLoading={isChipPosLoading}
                  isLocked={selectedRequirement.isLocked}
                  refreshData={mutateChipPos}
                  selectedMask={projectMasks.find((m) => m.id === selectedMaskId)}
                />
              )}

              {/* Step 4: Project Specs List */}
              {selectedRequirement?.id === 4 && selectedMaskId && (
                <ProjectSpecsListStep
                  error={specListsError}
                  isLoading={isSpecListsLoading}
                  isLocked={selectedRequirement.isLocked}
                  masterSpecExists={allSpecLists.some((s) => !s.masterVersion)}
                  openCreateModal={setIsCreateSpecListOpen}
                  specLists={specLists}
                />
              )}

              {/* Step 5: Test Labels */}
              {selectedRequirement?.id === 5 && selectedMaskId && (
                <TestLabelsStep
                  error={testLabelsError}
                  isLoading={isTestLabelsLoading}
                  isLocked={selectedRequirement.isLocked}
                  openTestFlightDialog={setIsTestFlightDialogOpen}
                  openTestLabelDialog={setIsCreateTestLabelOpen}
                  setTestLabelIdForTestFlight={setTestLabelIdForTestFlight}
                  testLabels={testLabels}
                />
              )}

              {/* Step 6: Measurement Requests */}
              {selectedRequirement?.id === 6 && (
                <MeasurementRequestStep
                  error={measurementRequestsError}
                  isLoading={isMeasurementRequestsLoading}
                  isLocked={selectedRequirement.isLocked}
                  measurementRequests={measurementRequests}
                  openMeasurementRequestDialog={setIsCreateMeasurementRequestDialogOpen}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Project Modal */}
      {project ? (
        <EditProjectDialog
          open={isEditProjectOpen}
          onOpenChange={setIsEditProjectOpen}
          project={project}
        />
      ) : null}

      {selectedMaskId && isCreateSpecListOpen ? (
        <CreateSpecListDialog
          maskId={selectedMaskId}
          specLists={allSpecLists}
          onOpenChange={setIsCreateSpecListOpen}
          open
          refetchSpecListsData={refetchSpecListsForSpecificMask}
        />
      ) : null}

      {isCreateTestLabelOpen ? (
        <CreateTestLabelDialog
          masks={allMasks}
          onOpenChange={setIsCreateTestLabelOpen}
          preSelectedMaskId={selectedMaskId}
          refetchData={mutateTestLabels}
        />
      ) : null}

      {selectedMaskId && isCreateMeasurementRequestDialogOpen ? (
        <CreateMeasurementRequestDialog
          chipPos={chipPos}
          maskId={selectedMaskId}
          onOpenChange={setIsCreateMeasurementRequestDialogOpen}
          open={isCreateMeasurementRequestDialogOpen}
          project={project}
          testLabels={testLabels}
        />
      ) : null}

      {/* Test Flight Request Dialog */}
      {selectedMaskId &&
        isTestFlightDialogOpen &&
        (() => {
          const selectedMask = projectMasks.find((m) => m.id === selectedMaskId);
          const unvalidatedTestLabel = testLabels.find(
            (label) => label.id === testLabelIdForTestFlight,
          );

          if (selectedMask && unvalidatedTestLabel) {
            return (
              <CreateTestFlightRequestDialog
                chipPos={chipPos}
                mask={selectedMask}
                onOpenChange={setIsTestFlightDialogOpen}
                open={isTestFlightDialogOpen}
                project={project}
                testLabel={unvalidatedTestLabel}
                testLabels={testLabels}
              />
            );
          }
          return null;
        })()}
    </div>
  );
}
